import type { JSX } from 'react';
import { FormSection, Radio } from '@ksyos/design-system';
import type { BariPreNurseSpecialistMedScreeningReportData } from '@common/validation/customControls/bariPre/bariPreNurseSpecialistMedScreeningReport/types';
import { ComorbiditeitenMedicatieOpties } from '@common/customControls/bariatrics';
import {
    useProcessActivityContext,
    useProcessActivityEditContext,
} from '@frontend/utils/hooks/useProcessActivityContext';
import {
    BooleanRadioGroupFormGroup,
    NumberInputColumnRowFormGroup,
    RadioGroupFormGroup,
} from '../../processActivityFormGroupFields';
import {
    sortedComorbiditeitenBehandelingenOpties,
    sortedComorbiditeitenMedicatieOpties,
    sortedDiabetesMellitusOpties,
    sortedLeverZiekteOpties,
    sortedMaligniteitenOpties,
} from './bariPreNurseSpecialistMedScreeningReportUtils';
import { BariPreNurseSpecialistMedScreeningReportComorbiditeitenAdditionalFieldsV2 } from './v2/BariPreNurseSpecialistMedScreeningReportComorbiditeitenAdditionalFieldsV2';

/**
 * @deprecated CS-20926: Clean up when version 1 is no longer in progress on production.
 * This means refactoring by removing conditionals from "chronische longziekte" and adding ComorbiditeitenAdditionalFieldsV2 to form.
 */
export function BariPreNurseSpecialistMedScreeningReportComorbiditeiten(): JSX.Element {
    const { customControlVersion } = useProcessActivityContext();
    const { data, onChange } = useProcessActivityEditContext<BariPreNurseSpecialistMedScreeningReportData>();

    return (
        <FormSection title="Comorbiditeiten">
            <BooleanRadioGroupFormGroup
                fieldKey="myocardinfarct"
                isRequired={true}
                label="Myocardinfarct"
                type="radio"
            />
            <BooleanRadioGroupFormGroup
                fieldKey="congestief_hartfalen"
                isRequired={true}
                label="Congestief hartfalen"
                type="radio"
            />
            <BooleanRadioGroupFormGroup
                fieldKey="perifeer_vaatlijden_aneurysma_aorta"
                isRequired={true}
                label="Perifeer vaatlijden/aneurysma aorta"
                type="radio"
            />
            <BooleanRadioGroupFormGroup
                fieldKey="cerebrovasculaire_aandoening"
                isRequired={true}
                label="Cerebrovasculaire aandoening"
                tooltipText="Onder andere CVA/TIA"
                type="radio"
            />
            <BooleanRadioGroupFormGroup
                fieldKey="dementie"
                isRequired={true}
                label="Dementie"
                tooltipText="Ziekte van Alzheimer, vasculaire dementie, frontotemporale dementie, Lewy body dementie, ziekte van Pick, Korsakov, Creutzfeldt-Jakob, Parkinson, posterieure corticale atrofie (PCA), Mild Cognitive Impairment (MCI)"
                type="radio"
            />
            <BooleanRadioGroupFormGroup
                fieldKey="chronische_longziekte"
                isClearable={customControlVersion !== 1}
                isRequired={customControlVersion === 1}
                label="Chronische longziekte"
                tooltipText="Astma, bronchiëctasieën, COPD (chronisch obstructieve longziekte), extrinsieke allergische alveolitis, (Idiopathische) longfibrose, mucoviscidose, sarcoïdose, slaapapneu"
                type="radio"
            />
            <BooleanRadioGroupFormGroup
                fieldKey="verhoogd_risico_DVT_of_PE"
                isRequired={true}
                label="Verhoogd risico DVT of PE"
                tooltipText="OAC, eerder doorgemaakte DVT of PE, chronische ziekten, roken, trombofilie, oudere leeftijd"
                type="radio"
            />
            <BooleanRadioGroupFormGroup
                fieldKey="gastrointestinaal_ulcuslijden"
                isRequired={true}
                label="Gastro-intestinaal ulcuslijden"
                type="radio"
            />
            <RadioGroupFormGroup
                fieldKey="leverziekte"
                isRequired={true}
                label="Leverziekte"
                tooltipText="Levercirrose, leververvetting, Nash"
            >
                {sortedLeverZiekteOpties.map((value) => (
                    <Radio
                        key={value}
                        label={value}
                        value={value}
                    />
                ))}
            </RadioGroupFormGroup>
            <BooleanRadioGroupFormGroup
                fieldKey="para_hemiplegie"
                isRequired={true}
                label="Para-/hemiplegie"
                type="radio"
            />
            <BooleanRadioGroupFormGroup
                fieldKey="nierziekte"
                isRequired={true}
                label="Nierziekte"
                tooltipText="Nierinsufficiëntie"
                type="radio"
            />
            <RadioGroupFormGroup
                fieldKey="maligniteit"
                isRequired={true}
                label="Maligniteit (excl. PCC of BCC van de huid)"
            >
                {sortedMaligniteitenOpties.map((value) => (
                    <Radio
                        key={value}
                        label={value}
                        value={value}
                    />
                ))}
            </RadioGroupFormGroup>
            <BooleanRadioGroupFormGroup
                fieldKey="HIV_AIDS"
                isRequired={true}
                label="HIV/AIDS"
                type="radio"
            />
            <BooleanRadioGroupFormGroup
                fieldKey="bindweefselziekte"
                isRequired={true}
                label="Bindweefselziekte (o.a. reumatoïde ziekten)"
                tooltipText="Reumatoïde ziekten, sclerodermie, Syndroom van Sjögren, Ehlers-Danlos"
                type="radio"
            />
            <RadioGroupFormGroup
                fieldKey="hypertensie_diagnose"
                isRequired={true}
                label="Hypertensie (diagnose)"
            >
                {sortedComorbiditeitenMedicatieOpties.map((value) => (
                    <Radio
                        key={value}
                        label={value}
                        value={value}
                    />
                ))}
            </RadioGroupFormGroup>
            <RadioGroupFormGroup
                fieldKey="diabetes_mellitus_diagnose"
                isRequired={true}
                label="Diabetes mellitus (diagnose)"
                onChange={handleChangeDiabetesMellitusDiagnose}
            >
                {sortedComorbiditeitenMedicatieOpties.map((value) => (
                    <Radio
                        key={value}
                        label={value}
                        value={value}
                    />
                ))}
            </RadioGroupFormGroup>
            {(data.diabetes_mellitus_diagnose === ComorbiditeitenMedicatieOpties.aanwezig ||
                data.diabetes_mellitus_diagnose === ComorbiditeitenMedicatieOpties.aanwezig_met_medicatie) && (
                <RadioGroupFormGroup
                    fieldKey="diabetes_mellitus_details"
                    isRequired={true}
                    label="Details diabetes mellitus"
                >
                    {sortedDiabetesMellitusOpties.map((value) => (
                        <Radio
                            key={value}
                            label={value}
                            value={value}
                        />
                    ))}
                </RadioGroupFormGroup>
            )}
            <NumberInputColumnRowFormGroup
                appendedLabel="mmol/mol"
                fieldKey="hba1c"
                label="HbA1c"
                maximumFractionDigits={2}
            />
            <RadioGroupFormGroup
                fieldKey="dyslipidemie"
                isRequired={true}
                label="Dyslipidemie (diagnose)"
            >
                {sortedComorbiditeitenMedicatieOpties.map((value) => (
                    <Radio
                        key={value}
                        label={value}
                        value={value}
                    />
                ))}
            </RadioGroupFormGroup>
            <NumberInputColumnRowFormGroup
                appendedLabel="mmol/l"
                fieldKey="LDL_cholesterol"
                label="LDL-cholesterol"
                maximumFractionDigits={2}
            />
            <NumberInputColumnRowFormGroup
                appendedLabel="mmol/l"
                fieldKey="HDL_cholesterol"
                label="HDL-cholesterol"
                maximumFractionDigits={2}
            />
            <NumberInputColumnRowFormGroup
                appendedLabel="mmol/l"
                fieldKey="triglyceriden"
                label="Triglyceriden"
                maximumFractionDigits={2}
            />
            <RadioGroupFormGroup
                fieldKey="GERD"
                isRequired={true}
                label="GERD (diagnose)"
            >
                {sortedComorbiditeitenMedicatieOpties.map((value) => (
                    <Radio
                        key={value}
                        label={value}
                        value={value}
                    />
                ))}
            </RadioGroupFormGroup>
            <RadioGroupFormGroup
                fieldKey="OSAS"
                isRequired={true}
                label="OSAS (diagnose)"
            >
                {sortedComorbiditeitenBehandelingenOpties.map((value) => (
                    <Radio
                        key={value}
                        label={value}
                        value={value}
                    />
                ))}
            </RadioGroupFormGroup>
            <RadioGroupFormGroup
                fieldKey="gewrichtsklachten"
                isRequired={true}
                label="Gewrichtsklachten (diagnose)"
            >
                {sortedComorbiditeitenBehandelingenOpties.map((value) => (
                    <Radio
                        key={value}
                        label={value}
                        value={value}
                    />
                ))}
            </RadioGroupFormGroup>

            {customControlVersion === 2 && (
                <BariPreNurseSpecialistMedScreeningReportComorbiditeitenAdditionalFieldsV2 />
            )}

            <BooleanRadioGroupFormGroup
                fieldKey="klachten_van_reflux"
                isRequired={true}
                label="Klachten van reflux"
                type="radio"
            />
            <BooleanRadioGroupFormGroup
                fieldKey="trombose"
                isRequired={true}
                label="Trombose"
                type="radio"
            />
        </FormSection>
    );

    async function handleChangeDiabetesMellitusDiagnose(value: string | null): Promise<void> {
        if (
            value === ComorbiditeitenMedicatieOpties.aanwezig ||
            value === ComorbiditeitenMedicatieOpties.aanwezig_met_medicatie ||
            value === ComorbiditeitenMedicatieOpties.geen
        ) {
            onChange(
                {
                    diabetes_mellitus_details: null,
                    diabetes_mellitus_diagnose: value,
                },
                'diabetes_mellitus_diagnose'
            );
        }
    }
}
